export type AccountState = 10 | 40;
export const ACCOUNT_STATES = [10, 40] as const satisfies readonly AccountState[];
/**
 * Steam 账号「在线」状态值 —— 只有在线账号才有可查的缓存会话。
 *
 * 原本它住在 `steamHistoryTab.ts`（那个文件已随新标签页方案退役），
 * 后来落到了 SteamHistoryPage.tsx —— 页面模块反过来被订单列表 /
 * 兑换工作台 import，方向是反的（领域值住在页面里）。现在归位到领域层。
 *
 * 两个调用方（订单列表 / 兑换工作台）都拿它当「历史」按钮的行状态门。
 */
export const ACCOUNT_ONLINE: AccountState = 40;
export type LoginStage = 10 | 20 | 30 | 32 | 40 | 60 | 80;
export const LOGIN_STAGES = [10, 20, 30, 32, 40, 60, 80] as const satisfies readonly LoginStage[];
// 50(=REVOKED/已失效) 随「下架/回架」一起退役（2026-10-20）：后端只留
// 可用(10)/已用(40) 两档，DB 里残留的 50 行由 alembic 0025 软删。
export type CdkeyState = 10 | 40;
export const CDKEY_STATES = [10, 40] as const satisfies readonly CdkeyState[];
// 85=RETRY_SCHEDULED / 86=DYNAMIC_FAIL 是 worker 兑换轮的真实生命周期值
//（限流/动态失败 → 治理拍重排），漏掉会渲染成「未知状态」。
export type RedeemState = 10 | 20 | 40 | 60 | 80 | 85 | 86;
export const REDEEM_STATES = [10, 20, 40, 60, 80, 85, 86] as const satisfies readonly RedeemState[];
/**
 * pipe 的 `protocol` 在 wire 上是自由字符串（`ProxyOut.protocol: string`），
 * 没有具名枚举 schema —— 这里以前端自己的清单为唯一权威，
 * `narrowProxyProtocol` 负责把 wire 值收窄回这个联合。
 */
export const PROXY_PROTOCOLS = [
  'vless',
  'vmess',
  'trojan',
  'ss',
  'hysteria2',
  'tuic',
  'wireguard',
  'ssh',
  'http',
  'socks',
  'anytls',
] as const;
export type ProxyProtocol = (typeof PROXY_PROTOCOLS)[number];
export type ProxyAccess = 'public' | 'private';
export const PROXY_ACCESSES = ['public', 'private'] as const satisfies readonly ProxyAccess[];
export const ROLE = {
  USER: 9,
  OPERATOR: 99,
  ADMIN: 999,
} as const;
export type Role = (typeof ROLE)[keyof typeof ROLE];
export const ROLES_BY_PRIVILEGE: readonly Role[] = [ROLE.ADMIN, ROLE.OPERATOR, ROLE.USER];
export function hasRoleAtLeast(actual: Role | undefined, required: Role): boolean {
  if (actual === undefined) return false;
  return actual >= required;
}
function isOneOf<T extends number>(value: number, allowed: readonly T[]): value is T {
  return (allowed as readonly number[]).includes(value);
}
export function narrowAccountState(value: number | null | undefined): AccountState | null {
  if (value === null || value === undefined) return null;
  return isOneOf(value, ACCOUNT_STATES) ? value : null;
}
export function narrowLoginStage(value: number | null | undefined): LoginStage {
  if (value === null || value === undefined) return 60;
  return isOneOf(value, LOGIN_STAGES) ? value : 60;
}
export function narrowLoginStageForDisplay(value: number | null | undefined): LoginStage | null {
  if (value === null || value === undefined) return null;
  return isOneOf(value, LOGIN_STAGES) ? value : null;
}
/**
 * 登录阶段 → 该阶段等哪种码。
 *
 * 它是 `LoginStage` 的**纯映射**（stage 30 等邮箱验证码 · 32 等令牌验证码 · 其余不
 * 等码），所以与枚举住在一起——原先它住在 `features/steamOrders/session.tsx`，
 * 于是事件流那个 feature 为了读它反过来 import 订单 feature，成了一对循环。
 */
export function codeFieldOf(stage: LoginStage): 'email_code' | 'twofactor_code' | null {
  if (stage === 30) return 'email_code';
  if (stage === 32) return 'twofactor_code';
  return null;
}
export function narrowCdkeyState(value: number | null | undefined): CdkeyState | null {
  if (value === null || value === undefined) return null;
  return isOneOf(value, CDKEY_STATES) ? value : null;
}
export function narrowRedeemState(value: number | null | undefined): RedeemState | null {
  if (value === null || value === undefined) return null;
  return isOneOf(value, REDEEM_STATES) ? value : null;
}
const PROXY_PROTOCOL_SET: ReadonlySet<string> = new Set<string>(PROXY_PROTOCOLS);
const PROXY_ACCESS_SET: ReadonlySet<string> = new Set<string>(PROXY_ACCESSES);
export function narrowProxyProtocol(value: string | null | undefined): ProxyProtocol | null {
  if (value === null || value === undefined) return null;
  return PROXY_PROTOCOL_SET.has(value) ? (value as ProxyProtocol) : null;
}
export function narrowProxyAccess(value: string | null | undefined): ProxyAccess | null {
  if (value === null || value === undefined) return null;
  return PROXY_ACCESS_SET.has(value) ? (value as ProxyAccess) : null;
}
